/**
 * Privacy-preserving visitor cadence tracking.
 *
 * Dedupe happens LOCALLY; the transmitted payload contains nothing
 * identifying — no id, no hash, no counter. Because the client guarantees
 * at most one `web_weekly_active` event per browser per ISO week, a plain
 * server-side row count IS the unique-browser count.
 *
 * Nothing read from localStorage is ever included in the payload.
 */
import { pulse } from "@/utils/pulse";

const WEEK_KEY = "pulse_dedupe_week";
const FIRSTRUN_KEY = "pulse_dedupe_firstrun";

/**
 * ISO-8601 week label, Monday-start, computed in UTC, formatted `YYYY-Www`.
 * The week belongs to the year containing its Thursday.
 */
export function isoWeekLabel(date: Date = new Date()): string {
  const d = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
  // Monday = 0 … Sunday = 6; shift to that week's Thursday.
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day + 3);
  const isoYear = d.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(isoYear, 0, 4));
  const firstDay = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDay + 3);
  const week =
    1 + Math.round((d.getTime() - firstThursday.getTime()) / (7 * 86400000));
  return `${isoYear}-W${String(week).padStart(2, "0")}`;
}

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): boolean {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function storageAvailable(): boolean {
  try {
    const probe = "__pulse_probe__";
    window.localStorage.setItem(probe, "1");
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

/**
 * Fires `web_first_run` at most once ever, and `web_weekly_active` at most
 * once per ISO week, per browser. Silently no-ops if localStorage is
 * unavailable — an unbounded event is worse than a missing one.
 */
export function trackVisitorCadence(): void {
  try {
    if (typeof window === "undefined") return;
    if (!storageAvailable()) return;

    if (!safeGet(FIRSTRUN_KEY)) {
      if (safeSet(FIRSTRUN_KEY, "1")) {
        pulse("web_first_run");
      }
    }

    const week = isoWeekLabel();
    if (safeGet(WEEK_KEY) !== week) {
      if (safeSet(WEEK_KEY, week)) {
        pulse("web_weekly_active");
      }
    }
  } catch {
    /* never throw into the host page */
  }
}
